import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  BookOpen, 
  Heart, 
  Users, 
  Sparkles, 
  ShoppingBag, 
  Eye,
  MessageSquare,
  Camera,
  Lock,
  ArrowRight,
  Play,
  FileText,
  ChevronRight
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getPortalSession, setPortalSession } from '../lib/familyPortalSession';

export default function FamilyPortalPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [story, setStory] = useState(null);
  const [portal, setPortal] = useState(null);
  const [stats, setStats] = useState({ responses: 0, photos: 0, contributions: 0 });
  
  // PIN verification
  const [requiresPin, setRequiresPin] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);

  // Cloud Functions
  const functions = getFunctions();
  const loadFamilyPortalFn = httpsCallable(functions, 'loadFamilyPortal');
  const verifyFamilyPortalPinFn = httpsCallable(functions, 'verifyFamilyPortalPin');

  useEffect(() => {
    loadPortal();
  }, [token]);

  const loadPortal = async () => {
    // The Cloud Function is the single entry point. It returns
    // { requiresPin: true } when the portal is PIN-protected and we don't yet
    // have a valid verification session, so the PIN can't be skipped and the
    // pinHash is never sent to the browser.
    await loadFullData();
  };

  const loadFullData = async () => {
    try {
      // =============================================
      // SECURITY: Use Cloud Function instead of direct
      // Firestore reads. Family members are unauthenticated —
      // Firestore rules block direct reads on familyPortals
      // and lifeStories. The Cloud Function uses the Admin SDK,
      // enforces the PIN, and never exposes pinHash.
      // =============================================
      const sessionToken = getPortalSession(token);
      const result = await loadFamilyPortalFn({ portalToken: token, sessionToken });

      if (result.data?.requiresPin) {
        setRequiresPin(true);
        setLoading(false);
        return;
      }

      if (!result.data?.success) {
        setError('Failed to load family portal.');
        setLoading(false);
        return;
      }

      setPortal(result.data.portal);
      setStory(result.data.story);
      setStats(result.data.stats);
      setRequiresPin(false);
      setLoading(false);
    } catch (err) {
      console.error('Error loading portal data:', err);
      if (err.code === 'not-found' || err.code === 'functions/not-found') {
        setError('This family portal link is invalid or has expired.');
      } else {
        setError('Failed to load life story. Please try again.');
      }
      setLoading(false);
    }
  };

  const verifyPin = async () => {
    if (pinInput.length !== 4) return;
    
    try {
      // =============================================
      // SECURITY FIX: Use Cloud Function for PIN
      // verification instead of client-side hash
      // comparison. The old approach exposed the
      // pinHash to the client and used insecure
      // base64 comparison.
      // =============================================
      const result = await verifyFamilyPortalPinFn({ token, pin: pinInput });

      if (result.data.verified) {
        // Persist the verification session so loadFamilyPortal will return data.
        if (result.data.sessionToken) {
          setPortalSession(token, result.data.sessionToken);
        }
        setPinVerified(true);
        setRequiresPin(false);
        setLoading(true);
        await loadFullData();
      } else {
        setPinError(true);
        setPinInput('');
      }
    } catch (err) {
      console.error('PIN verification error:', err);
      if (err.code === 'functions/resource-exhausted') {
        setError('Too many attempts. Please try again in 5 minutes.');
      } else {
        setPinError(true);
        setPinInput('');
      }
    }
  };

  const getDisplayName = () => {
    if (!story) return '';
    // Check multiple possible field names for the resident's name
    const name = story.subjectName || story.preferredName || story.residentName || '';
    if (story.coupleMode && story.partnerName) {
      return `${name} & ${story.partnerName}`;
    }
    return name;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading family portal...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Unable to Access</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-quiltt-purple text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
          >
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  // PIN verification screen
  if (requiresPin && !pinVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-quiltt-purple" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Enter PIN</h1>
            <p className="text-gray-600">This family portal is protected. Enter the 4-digit PIN to continue.</p>
          </div>
          
          <div className="mb-6">
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={pinInput}
              onChange={(e) => {
                setPinInput(e.target.value.replace(/\D/g, ''));
                setPinError(false);
              }}
              placeholder="••••"
              className={`w-full px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono border-2 rounded-xl ${
                pinError ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
              autoFocus
            />
            {pinError && (
              <p className="text-red-500 text-sm mt-2 text-center">Incorrect PIN. Please try again.</p>
            )}
          </div>
          
          <button
            onClick={verifyPin}
            disabled={pinInput.length !== 4}
            className="w-full py-3 bg-quiltt-purple hover:bg-purple-700 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Main portal view
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-quiltt-purple to-purple-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Life Story</p>
              <h1 className="font-bold text-gray-900">{getDisplayName()}</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero card with photo */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-quiltt-purple to-purple-600 p-6 text-white">
            <div className="flex items-center gap-4">
              {story?.profilePhotoUrl ? (
                <img
                  src={story.profilePhotoUrl}
                  alt={getDisplayName()}
                  className="w-20 h-20 rounded-full object-cover border-4 border-white/30"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                  <Heart className="w-10 h-10 text-white/80" />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold">{getDisplayName()}'s Life Story</h2>
                <p className="text-white/80">Family Portal</p>
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 divide-x">
            <div className="p-4 text-center">
              <p className="text-2xl font-bold text-quiltt-purple">{stats.responses}</p>
              <p className="text-sm text-gray-500">Responses</p>
            </div>
            <div className="p-4 text-center">
              <p className="text-2xl font-bold text-quiltt-purple">{stats.contributions}</p>
              <p className="text-sm text-gray-500">Contributions</p>
            </div>
            <div className="p-4 text-center">
              <p className="text-2xl font-bold text-quiltt-purple">{stats.photos}</p>
              <p className="text-sm text-gray-500">Photos</p>
            </div>
          </div>
        </div>

        {/* Action cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* View Story */}
          {portal?.permissions?.viewResponses && (
            <ActionCard
              icon={Eye}
              title="View Story"
              description="Read all recorded memories and responses"
              onClick={() => navigate(`/family/${token}/story`)}
              color="purple"
            />
          )}
          
          {/* Add Memory */}
          <ActionCard
            icon={MessageSquare}
            title="Add a Memory"
            description="Share your own stories and memories"
            onClick={() => navigate(`/family/${token}/contribute`)}
            color="pink"
          />
          
          {/* AI Summaries */}
          {portal?.permissions?.viewSummaries && (
            <ActionCard
              icon={Sparkles}
              title="AI Summaries"
              description="View biography, Quiltt Card & conversation starters"
              onClick={() => navigate(`/family/${token}/summaries`)}
              color="blue"
            />
          )}
          
          {/* Preview Book */}
          {portal?.permissions?.viewBookPreview && (
            <ActionCard
              icon={BookOpen}
              title="Preview Book"
              description="See how the printed keepsake book will look"
              onClick={() => navigate(`/family/${token}/book`)}
              color="amber"
            />
          )}
          
          {/* Order Book */}
          {portal?.permissions?.canOrderBook && (
            <ActionCard
              icon={ShoppingBag}
              title="Order a Book"
              description="Purchase a beautiful printed keepsake"
              onClick={() => navigate(`/family/${token}/book?order=true`)}
              color="green"
              highlight
            />
          )}
          
          {/* Add Photos */}
          <ActionCard
            icon={Camera}
            title="Add Photos"
            description="Upload photos to include in the story"
            onClick={() => navigate(`/family/${token}/contribute?type=photo`)}
            color="rose"
          />
        </div>

        {/* Footer info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            This family portal was shared with you by {story?.createdByName || 'the story creator'}.
          </p>
          <p className="mt-1">
            Powered by <a href="https://quiltt.com" className="text-quiltt-purple hover:underline">Quiltt</a>
          </p>
        </div>
      </main>
    </div>
  );
}

function ActionCard({ icon: Icon, title, description, onClick, color, highlight }) {
  const colors = {
    purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-100',
    pink: 'bg-pink-50 text-pink-600 group-hover:bg-pink-100',
    blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100',
    amber: 'bg-amber-50 text-amber-600 group-hover:bg-amber-100',
    green: 'bg-green-50 text-green-600 group-hover:bg-green-100',
    rose: 'bg-rose-50 text-rose-600 group-hover:bg-rose-100'
  };

  return (
    <button
      onClick={onClick}
      className={`group bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all text-left w-full ${
        highlight ? 'ring-2 ring-green-500 ring-offset-2' : ''
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 group-hover:text-quiltt-purple transition-colors">
            {title}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-quiltt-purple transition-colors" />
      </div>
    </button>
  );
}
